import React, { useState, useEffect } from 'react';
import { X, Check, Globe } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { COUNTRIES, groceryStoresByCountry, type GroceryStore } from '../types/recipe';

interface OnboardingModalProps {
  onComplete: () => void;
}

export function OnboardingModal({ onComplete }: OnboardingModalProps) {
  const [selectedCountry, setSelectedCountry] = useState<keyof typeof COUNTRIES>('US');
  const [selectedStores, setSelectedStores] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    // Get the current user's ID when the component mounts
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (user) {
        setUserId(user.id);
      }
    });
  }, []);

  const handleStoreToggle = (storeId: string) => {
    const newSelected = new Set(selectedStores);
    if (newSelected.has(storeId)) {
      newSelected.delete(storeId);
    } else {
      newSelected.add(storeId);
    }
    setSelectedStores(newSelected);
  };

  const handleSubmit = async () => {
    if (!userId) {
      setError('You must be logged in to continue');
      return;
    }

    if (selectedStores.size < 3) {
      setError('Please select at least 3 stores');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const stores = groceryStoresByCountry[selectedCountry]
        .filter(store => selectedStores.has(store.id))
        .map((store, index) => ({
          user_id: userId,
          store_id: store.id,
          store_name: store.name,
          search_url: store.searchUrl,
          logo_url: store.logo,
          country: store.country,
          is_default: index === 0 // First store becomes default
        }));

      const { error: insertError } = await supabase
        .from('user_grocery_stores')
        .insert(stores);

      if (insertError) throw insertError;
      onComplete();
    } catch (err) {
      console.error('Error saving stores:', err);
      setError(err instanceof Error ? err.message : 'Failed to save stores');
    } finally {
      setLoading(false);
    }
  };

  // Separate stores by type
  const supermarkets = groceryStoresByCountry[selectedCountry].filter(store => store.type === 'supermarket');
  const deliveryServices = groceryStoresByCountry[selectedCountry].filter(store => store.type === 'delivery');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-4 md:p-6 space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Globe className="w-8 h-8 text-blue-600" />
              <h2 className="text-2xl font-bold">Welcome to Recipe Manager!</h2>
            </div>
          </div>

          <p className="text-gray-600">
            To help you get started, please select at least 3 stores where you regularly shop. 
            This will help us provide quick ingredient search options when planning your meals.
          </p>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Your Country
              </label>
              <select
                value={selectedCountry}
                onChange={(e) => setSelectedCountry(e.target.value as keyof typeof COUNTRIES)}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {Object.entries(COUNTRIES).map(([code, name]) => (
                  <option key={code} value={code}>
                    {name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-6">
              {/* Supermarkets Section */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Supermarkets</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {supermarkets.map((store) => (
                    <button
                      key={store.id}
                      onClick={() => handleStoreToggle(store.id)}
                      className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                        selectedStores.has(store.id)
                          ? 'bg-blue-50 border-blue-200'
                          : 'hover:bg-gray-50 border-gray-300'
                      }`}
                    >
                      {store.logo && (
                        <img
                          src={store.logo}
                          alt={store.name}
                          className="w-6 h-6 object-contain"
                        />
                      )}
                      <span className="flex-1 text-left font-medium">
                        {store.name}
                      </span>
                      {selectedStores.has(store.id) && (
                        <Check size={20} className="text-blue-600" />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Delivery Services Section */}
              <div>
                <h3 className="text-lg font-semibold mb-3">Delivery Services</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {deliveryServices.map((store) => (
                    <button
                      key={store.id}
                      onClick={() => handleStoreToggle(store.id)}
                      className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                        selectedStores.has(store.id)
                          ? 'bg-blue-50 border-blue-200'
                          : 'hover:bg-gray-50 border-gray-300'
                      }`}
                    >
                      {store.logo && (
                        <img
                          src={store.logo}
                          alt={store.name}
                          className="w-6 h-6 object-contain"
                        />
                      )}
                      <span className="flex-1 text-left font-medium">
                        {store.name}
                      </span>
                      {selectedStores.has(store.id) && (
                        <Check size={20} className="text-blue-600" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-between items-center gap-3 pt-4 border-t">
            <div className="text-sm text-gray-600">
              Selected: {selectedStores.size} stores (minimum 3 required)
            </div>
            <button
              onClick={handleSubmit}
              disabled={loading || selectedStores.size < 3}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Continue'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}